import { useId, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ExpressionIssue } from "@repo/shared";
import {
  Badge,
  Button,
  Chip,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type BadgeProps,
} from "@h2j-data-team/ui";

/*
 * Pieces shared by the five analysis tables (fields, variables, dimensions,
 * measures, objects). They have an expandable row, so they are composed from the
 * library's Table primitives instead of a DataTable; this frame gives them the
 * same card, toolbar and footer a DataTable has, and the row below makes the
 * expansion a real button.
 */

/** The card around an analysis table: toolbar, a table that scrolls on its own, and a footer line. */
export function AnalysisTableCard({
  toolbar,
  footer,
  children,
}: {
  toolbar: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-[22rem] min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-hairline bg-surface shadow-sm">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-hairline px-4 py-3">
        {toolbar}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      {footer && (
        <div className="shrink-0 border-t border-hairline px-4 py-2.5 text-xs text-dim">
          {footer}
        </div>
      )}
    </div>
  );
}

/** Search box that takes the free width of the toolbar, like a DataTable's. */
export const SEARCH_CLASS = "flex-1 basis-60 min-w-[min(15rem,100%)]";

/**
 * The expandable row: a toggle button in the first cell (the keyboard and
 * screen-reader path, `aria-expanded`), the row's own cells as children, and the
 * detail row right under it while expanded. A click anywhere on the row outside
 * a control also toggles it, as a DataTable's `rowLink` does for a link.
 */
export function ExpandableRow({
  name,
  expandable,
  expanded,
  onToggle,
  colSpan,
  detail,
  children,
}: {
  /** Names the toggle button for assistive technology. */
  name: string;
  expandable: boolean;
  expanded: boolean;
  onToggle: () => void;
  /** Columns of the table, toggle cell included. */
  colSpan: number;
  detail: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation("analyses");
  const detailId = useId();
  const open = expandable && expanded;

  return (
    <>
      <TableRow
        className={expandable ? "cursor-pointer" : undefined}
        onClick={(event) => {
          if (!expandable) return;
          if ((event.target as HTMLElement).closest("button, a, input, [role='button']")) return;
          if (window.getSelection()?.toString()) return;
          onToggle();
        }}
      >
        <TableCell className="px-2">
          {expandable && (
            <Button
              variant="ghost"
              size="icon-compact"
              className="size-5"
              aria-expanded={open}
              aria-controls={open ? detailId : undefined}
              aria-label={t("table.rowDetails", { name })}
              onClick={onToggle}
            >
              {open ? <ChevronDown /> : <ChevronRight />}
            </Button>
          )}
        </TableCell>
        {children}
      </TableRow>
      {open && (
        <TableRow id={detailId}>
          <TableCell
            colSpan={colSpan}
            wrap
            variant="identity"
            className="bg-surface-muted/50 py-3 ps-12"
          >
            {detail}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** A short flag on a row ("indirect", "orphan"…) whose meaning shows on hover and on keyboard focus. */
export function FlagBadge({
  label,
  hint,
  variant = "outline",
}: {
  label: string;
  hint: string;
  variant?: BadgeProps["variant"];
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={variant} tabIndex={0} className="whitespace-nowrap">
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{hint}</TooltipContent>
    </Tooltip>
  );
}

/** The issues of a variable, dimension or measure expression, shown in its expanded row. */
export function ExpressionIssues({
  scope,
  issues,
}: {
  scope: "variables" | "dimensions" | "measures";
  issues: ExpressionIssue;
}) {
  const { t } = useTranslation("analyses");
  const bad = issues.badFieldNames;
  const dangerous = issues.dangerousFieldNames;
  return (
    <div className="mb-3 space-y-2">
      {issues.errorMsg && (
        <div>
          <p className="mb-1 text-xs font-medium text-status-danger-foreground">
            {t(`${scope}.expressionError`)}
          </p>
          <p className="font-mono text-xs break-words text-status-danger-foreground">
            {issues.errorMsg}
          </p>
        </div>
      )}
      {bad.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-status-danger-foreground">
            {t(`${scope}.badFields`)}
          </p>
          <div className="flex flex-wrap gap-1">
            {bad.map((name, i) => (
              <Chip key={i} variant="danger">
                {name}
              </Chip>
            ))}
          </div>
        </div>
      )}
      {dangerous.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-status-warning-foreground">
            {t(`${scope}.dangerousFields`)}
          </p>
          <div className="flex flex-wrap gap-1">
            {dangerous.map((name, i) => (
              <Chip key={i} variant="warning">
                {name}
              </Chip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
